import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, Sparkles } from "lucide-react";
import api, { getErr } from "../../lib/api";
import { NeonButton, NeonCard, Spinner, Pill } from "../../components/Neon";

const TYPES = [
  { v: "none", label: "No Win" },
  { v: "discount", label: "Discount %" },
  { v: "free_ticket", label: "Free ticket" },
];
const blank = () => ({ label: "New wedge", type: "none", value: 0, weight: 10, color: "#161616", competition_id: "" });

export default function AdminSpinWheel() {
  const [enabled, setEnabled] = useState(true);
  const [noLossDate, setNoLossDate] = useState("");
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/spin-wheel").then(({ data }) => {
      setEnabled(!!data.enabled);
      setNoLossDate(data.no_loss_date ? String(data.no_loss_date).slice(0, 10) : "");
      setSegments((data.segments || []).map((s) => ({ ...s, competition_id: s.competition_id || "" })));
    }).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const upd = (i, k, v) => setSegments((arr) => arr.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)));
  const addSeg = () => setSegments((arr) => [...arr, blank()]);
  const delSeg = (i) => setSegments((arr) => arr.filter((_, idx) => idx !== i));

  const save = async () => {
    if (segments.length < 2) { toast.error("Add at least 2 wedges."); return; }
    setSaving(true);
    try {
      await api.put("/admin/spin-wheel", {
        enabled,
        no_loss_date: noLossDate || null,
        segments: segments.map((s) => ({
          label: s.label, type: s.type, value: Number(s.value) || 0,
          weight: Number(s.weight) || 1, color: s.color || "#161616",
          competition_id: s.competition_id || null,
        })),
      });
      toast.success("Spin wheel saved.");
      load();
    } catch (e) { toast.error(getErr(e)); } finally { setSaving(false); }
  };

  if (loading) return <Spinner />;
  const noWinCount = segments.filter((s) => s.type === "none").length;

  return (
    <div data-testid="admin-spinwheel" className="space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="font-display uppercase text-2xl text-white flex items-center gap-2"><Sparkles className="h-5 w-5 text-[var(--neon-cyan)]" /> Spin Wheel Editor</h2>
        <NeonButton variant="pink" onClick={save} loading={saving} data-testid="spinwheel-save"><Save className="h-4 w-4" /> Save Wheel</NeonButton>
      </div>

      <NeonCard variant="flow" innerClassName="p-5">
        <div className="flex flex-wrap items-end gap-5">
          <label className="flex items-center gap-2 text-white"><input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid="spinwheel-enabled" className="h-5 w-5 accent-[var(--neon-cyan)]" /> Wheel enabled</label>
          <div>
            <label className="text-xs uppercase tracking-widest text-[var(--text-muted)]">"Everyone wins" day (no-loss date)</label>
            <input type="date" value={noLossDate} onChange={(e) => setNoLossDate(e.target.value)} data-testid="spinwheel-noloss" className="mt-1 block bg-[var(--surface-2)] border border-white/10 rounded-lg px-3 py-2 text-white text-sm" />
          </div>
          <Pill color={noWinCount >= 1 ? "grey" : "gold"}>{noWinCount} “No Win” wedge{noWinCount === 1 ? "" : "s"}</Pill>
        </div>
        <p className="mt-2 text-xs text-[var(--text-muted)]">On the no-loss date, every “No Win” wedge is dropped so every spin wins (guaranteed_win). Default wheel should carry three “No Win” wedges.</p>
      </NeonCard>

      <NeonCard variant="soft" innerClassName="p-0">
        <div className="overflow-x-auto p-4"><table className="w-full text-sm">
          <thead><tr className="text-left text-[var(--text-muted)] border-b border-white/10">
            <th className="py-2 pr-2">Label</th><th className="pr-2">Type</th><th className="pr-2">Value</th><th className="pr-2">Weight</th><th className="pr-2">Colour</th><th className="pr-2">Comp ID (optional)</th><th></th>
          </tr></thead>
          <tbody data-testid="spinwheel-rows">
            {segments.map((s, i) => (
              <tr key={i} className="border-b border-white/5">
                <td className="py-2 pr-2"><input value={s.label} onChange={(e) => upd(i, "label", e.target.value)} className="w-36 bg-[var(--surface-2)] border border-white/10 rounded px-2 py-1.5 text-white" data-testid={`seg-label-${i}`} /></td>
                <td className="pr-2"><select value={s.type} onChange={(e) => upd(i, "type", e.target.value)} className="bg-[var(--surface-2)] border border-white/10 rounded px-2 py-1.5 text-white" data-testid={`seg-type-${i}`}>{TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}</select></td>
                <td className="pr-2"><input type="number" value={s.value} onChange={(e) => upd(i, "value", e.target.value)} className="w-20 bg-[var(--surface-2)] border border-white/10 rounded px-2 py-1.5 text-white" /></td>
                <td className="pr-2"><input type="number" value={s.weight} onChange={(e) => upd(i, "weight", e.target.value)} className="w-20 bg-[var(--surface-2)] border border-white/10 rounded px-2 py-1.5 text-white" /></td>
                <td className="pr-2"><input type="color" value={s.color} onChange={(e) => upd(i, "color", e.target.value)} className="h-8 w-12 bg-transparent border border-white/10 rounded" /></td>
                <td className="pr-2"><input value={s.competition_id} onChange={(e) => upd(i, "competition_id", e.target.value)} placeholder="—" className="w-44 bg-[var(--surface-2)] border border-white/10 rounded px-2 py-1.5 text-white font-mono text-xs" /></td>
                <td><button onClick={() => delSeg(i)} aria-label="delete" data-testid={`seg-del-${i}`} className="text-[var(--text-muted)] hover:text-[var(--error,#ff4d6d)]"><Trash2 className="h-4 w-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table></div>
        <div className="p-4 pt-0"><NeonButton variant="ghost" onClick={addSeg} data-testid="spinwheel-add"><Plus className="h-4 w-4" /> Add wedge</NeonButton></div>
      </NeonCard>
    </div>
  );
}
